import React from 'react'
import styled from 'styled-components'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

export type BrandDatePickerProps = {
  value?: string | null
  onChange: (value: string) => void
  minDate?: string | Dayjs
  maxDate?: string | Dayjs
  placeholder?: string
  disabled?: boolean
  required?: boolean
  name?: string
  /** compact = table/filter height (36px), default = form field (44px) */
  size?: 'default' | 'compact'
  fullWidth?: boolean
  className?: string
  disablePast?: boolean
}

const toDayjs = (v?: string | Dayjs | null): Dayjs | null => {
  if (!v) return null
  if (dayjs.isDayjs(v)) return v.isValid() ? v : null
  const d = dayjs(v)
  return d.isValid() ? d : null
}

const BrandDatePicker: React.FC<BrandDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = 'dd/mm/yyyy',
  disabled,
  required,
  name,
  size = 'default',
  fullWidth = true,
  className,
  disablePast,
}) => {
  const resolvedMin = disablePast
    ? dayjs().startOf('day')
    : toDayjs(minDate ?? null) ?? undefined

  return (
    <Wrap $size={size} $fullWidth={fullWidth} className={className}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={toDayjs(value)}
          format="DD/MM/YYYY"
          desktopModeMediaQuery="@media (min-width: 768px)"
          disabled={disabled}
          minDate={resolvedMin}
          maxDate={toDayjs(maxDate ?? null) ?? undefined}
          onChange={(e) => {
            if (e && e.isValid()) onChange(e.format('YYYY-MM-DD'))
            else onChange('')
          }}
          slotProps={
            {
              popper: {
                strategy: 'fixed',
                sx: { zIndex: 14000 },
              },
              desktopPaper: {
                sx: { zIndex: 14000 },
              },
              textField: {
                name,
                required,
                placeholder,
                fullWidth,
                size: size === 'compact' ? 'small' : 'medium',
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  ;(e.currentTarget as HTMLElement)
                    .querySelector('button')
                    ?.click()
                },
              },
            } as any
          }
        />
      </LocalizationProvider>
    </Wrap>
  )
}

export default BrandDatePicker

const Wrap = styled.div<{ $size: 'default' | 'compact'; $fullWidth: boolean }>`
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  .MuiFormControl-root {
    width: 100%;
    background: #fff;
    border-radius: ${({ $size }) => ($size === 'compact' ? '10px' : '12px')};
  }

  .MuiInputBase-root {
    height: ${({ $size }) => ($size === 'compact' ? '36px' : '44px')};
    font-family: Montserrat, sans-serif;
    font-size: ${({ $size }) => ($size === 'compact' ? '13px' : '14px')};
    font-weight: 500;
    cursor: pointer;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 1px solid rgba(23, 23, 23, 0.12) !important;
    border-radius: ${({ $size }) => ($size === 'compact' ? '10px' : '12px')};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(201, 77, 170, 0.45) !important;
  }

  .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(201, 77, 170, 0.55) !important;
    border-width: 1px !important;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }

  .MuiOutlinedInput-input {
    padding: 0 12px;
    height: 100%;
    cursor: pointer;
    box-sizing: border-box;
  }

  .MuiInputAdornment-root .MuiSvgIcon-root {
    color: #c94daa;
    font-size: ${({ $size }) => ($size === 'compact' ? '18px' : '20px')};
  }
`
